// ModalConfiguracaoModelo.jsx
// Modal reutilizável para personalizar, por empresa, o texto de um modelo de documento
// (Procuração, Contrato ou Documento Complementar). Usado em Procuracao.jsx, Contratos.jsx
// e DocumentosComplementares.jsx.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { FaTimes, FaSave, FaUndo } from 'react-icons/fa';
import config from '../config/config';
import { useToast } from './ToastContext';

const { API_BASE_URL } = config;

const TEAL = '#0f766e';
const TEAL_DARK = '#115e59';
const TEAL_LIGHT = '#f0fdfa';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2500;
  padding: 16px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
`;

const Titulo = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #134e4a;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #34495e;
  margin-bottom: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 260px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Courier New', monospace;
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;
`;

const Badge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 10px;
  margin-bottom: 10px;
  background: ${props => (props.$customizado ? '#fef3cd' : '#ecf0f1')};
  color: ${props => (props.$customizado ? '#b7791f' : '#7f8c8d')};
`;

const TokensBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background: ${TEAL_LIGHT};
  border-radius: 6px;
  font-size: 12px;
  color: #134e4a;
`;

const TokenChip = styled.code`
  background: white;
  border: 1px solid #99f6e4;
  border-radius: 4px;
  padding: 1px 5px;
  margin: 2px;
  display: inline-block;
  font-size: 11px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #eee;
`;

const BotaoPrimario = styled.button`
  background: ${TEAL};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover { background: ${TEAL_DARK}; }
  &:disabled { background: #9ca3af; cursor: default; }
`;

const BotaoSecundario = styled.button`
  background: white;
  color: #6b7280;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalConfiguracaoModelo = ({ categoria, modelos, tokensAjuda, buscarTextoPadrao, onClose }) => {
  const [modeloSelecionado, setModeloSelecionado] = useState(modelos[0]?.tipo || '');
  const [texto, setTexto] = useState('');
  const [customizado, setCustomizado] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const { addToast } = useToast();

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const carregarTexto = useCallback(async (tipo) => {
    setCarregando(true);
    try {
      const resp = await axios.get(`${API_BASE_URL}/modelos-documento-texto`, {
        ...getAuthHeader(),
        params: { categoria, modelo: tipo }
      });
      if (resp.status === 200 && resp.data && resp.data.texto != null) {
        setTexto(resp.data.texto);
        setCustomizado(true);
      } else {
        setCustomizado(false);
        const padrao = await buscarTextoPadrao(tipo);
        setTexto(padrao || '');
      }
    } catch (err) {
      addToast('error', 'Erro ao carregar o texto do modelo');
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoria]);

  useEffect(() => {
    if (modeloSelecionado) carregarTexto(modeloSelecionado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeloSelecionado]);

  const salvar = async () => {
    setSalvando(true);
    try {
      await axios.put(`${API_BASE_URL}/modelos-documento-texto`,
        { categoria, modelo: modeloSelecionado, texto },
        getAuthHeader()
      );
      setCustomizado(true);
      addToast('success', 'Texto do modelo salvo com sucesso!');
    } catch (err) {
      addToast('error', 'Erro ao salvar o texto do modelo');
    } finally {
      setSalvando(false);
    }
  };

  const restaurarPadrao = async () => {
    setSalvando(true);
    try {
      await axios.delete(`${API_BASE_URL}/modelos-documento-texto`, {
        ...getAuthHeader(),
        params: { categoria, modelo: modeloSelecionado }
      });
      setCustomizado(false);
      const padrao = await buscarTextoPadrao(modeloSelecionado);
      setTexto(padrao || '');
      addToast('success', 'Modelo restaurado ao padrão do sistema!');
    } catch (err) {
      addToast('error', 'Erro ao restaurar o padrão');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Header>
          <Titulo>Personalizar texto do modelo</Titulo>
          <CloseButton onClick={onClose}><FaTimes /></CloseButton>
        </Header>
        <Body>
          <Label>Modelo</Label>
          <Select value={modeloSelecionado} onChange={(e) => setModeloSelecionado(e.target.value)} disabled={salvando}>
            {modelos.map(m => (
              <option key={m.tipo} value={m.tipo}>{m.titulo}</option>
            ))}
          </Select>

          <Badge $customizado={customizado}>
            {customizado ? 'Texto personalizado' : 'Usando o texto padrão do sistema'}
          </Badge>

          <Label>Texto que será impresso no documento</Label>
          <Textarea
            value={carregando ? 'Carregando...' : texto}
            onChange={(e) => setTexto(e.target.value)}
            disabled={carregando || salvando}
          />

          {tokensAjuda?.length > 0 && (
            <TokensBox>
              Use estes campos onde quiser inserir dados do documento automaticamente:{' '}
              {tokensAjuda.map(t => <TokenChip key={t}>{`{{${t}}}`}</TokenChip>)}
            </TokensBox>
          )}
        </Body>
        <Footer>
          <BotaoSecundario onClick={restaurarPadrao} disabled={salvando || carregando || !customizado}>
            <FaUndo /> Restaurar padrão
          </BotaoSecundario>
          <BotaoPrimario onClick={salvar} disabled={salvando || carregando}>
            <FaSave /> {salvando ? 'Salvando...' : 'Salvar'}
          </BotaoPrimario>
        </Footer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ModalConfiguracaoModelo;
